import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Lock } from "lucide-react";

const TIME_SLOTS = ["10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00"];

export const ConsultationBooking = () => {
  const [avail, setAvail] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("11:00");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/consultations/availability");
      setAvail(data);
    } catch { /* degrade silently */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const book = async () => {
    if (!date) { toast.error("Pick a date first"); return; }
    setBusy(true);
    try {
      await api.post("/consultations/book", { date, time });
      toast.success(`Consultation with K Singh confirmed for ${date} at ${time} IST`);
      setDate("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  if (!avail) return null;
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  return (
    <div className="border border-stone-200 rounded-2xl bg-white p-6" data-testid="consultation-section">
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className="text-amber-700" />
        <div className="overline">Book a Consultation</div>
      </div>
      <p className="text-xs text-stone-500 mt-2">30-minute private session with <span className="font-medium text-[#1E1B4B]">K Singh</span>, Anubandh relationship expert.</p>

      {avail.locked ? (
        <div className="mt-4 rounded-xl border border-stone-200 bg-[#F9F8F6] p-4" data-testid="consultation-locked">
          <div className="flex items-center gap-2 text-stone-600 text-sm"><Lock size={14} /> Available on Premium &amp; VIP</div>
          <p className="text-xs text-stone-500 mt-2">Premium: one session every 15 days. VIP: one session every week.</p>
          <Link to="/subscription" data-testid="consultation-upgrade-cta"
            className="btn-pill inline-flex mt-3 text-white text-xs px-4 py-2" style={{ background: "#D97706" }}>
            Upgrade to unlock
          </Link>
        </div>
      ) : (
        <div className="mt-4">
          <div className="text-xs text-stone-500">
            Your plan: <span className="capitalize font-medium text-[#1E1B4B]">{avail.tier === "vip" ? "VIP" : avail.tier}</span> · 1 session every {avail.window_days} days
          </div>
          {avail.eligible ? (
            <div className="mt-3 space-y-2">
              <input type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="consultation-date-input"
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white" />
              <select value={time} onChange={(e) => setTime(e.target.value)}
                data-testid="consultation-time-select"
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white">
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{t} IST</option>)}
              </select>
              <button onClick={book} disabled={busy} data-testid="consultation-book-btn"
                className="btn-pill w-full text-sm text-white py-2.5" style={{ background: "#1E1B4B" }}>
                {busy ? "Booking…" : "Confirm session"}
              </button>
            </div>
          ) : (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="consultation-next-eligible">
              Next session available from <strong>{avail.next_eligible_date}</strong>.
            </div>
          )}
          {avail.bookings?.length > 0 && (
            <div className="mt-4 border-t border-stone-100 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-400">Your sessions</div>
              {avail.bookings.slice(0, 3).map((b) => (
                <div key={b.id} className="text-xs text-stone-600 mt-1.5" data-testid={`consultation-booking-${b.id}`}>
                  {b.scheduled_date} · {b.scheduled_time} IST — with {b.expert}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
